import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProductCard from "../../custom/product-card/product-card";
import FeatureCard from "../../custom/feature-card/feature-card";
import {
  RECOMMEND_KEYWORDS,
  TRENDING,
  searchProducts,
} from "../../../constant/search-content";
import "./search-page-style.scss";

//搜尋
//
//  兩種狀態：
//    沒有關鍵字 → 推薦關鍵字 + 即時熱門（讓人知道能搜什麼）
//    有關鍵字   → 結果清單，沒有結果就給明確的空狀態
//
//  關鍵字放在網址的 query string（?q=），不是只存在 state：
//  這樣搜尋結果可以被分享、可以用上一頁回到前一次的搜尋，
//  重新整理也不會把輸入弄丟。
const SearchPage = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  //輸入框的即時值與網址的值分開：打字時不該每個字都塞一筆瀏覽紀錄
  const queryFromUrl = params.get("q") || "";
  const [keyword, setKeyword] = useState(queryFromUrl);

  //從別處帶著 ?q= 進來（例如點推薦關鍵字後重整）時要同步回輸入框
  useEffect(() => {
    setKeyword(queryFromUrl);
  }, [queryFromUrl]);

  //進頁就聚焦：使用者點「搜尋」就是要打字，少一次點擊
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  //目前資料量很小，直接算即可；之後換成 API 時這裡改成 useEffect + 防抖
  const results = useMemo(() => searchProducts(keyword), [keyword]);

  const hasKeyword = keyword.trim().length > 0;

  //送出搜尋：把關鍵字寫進網址
  //  replace: true —— 每打一次搜尋不該累積一堆返回紀錄，
  //  按上一頁應該要離開搜尋頁，而不是逐字倒退
  const submit = (value) => {
    const v = value.trim();
    setKeyword(value);
    if (v) setParams({ q: v }, { replace: true });
    else setParams({}, { replace: true });
  };

  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  const handleClear = () => {
    submit("");
    inputRef.current?.focus();
  };

  return (
    <main className="sp">
      <header className="sp__header">
        <button type="button" className="sp__back" onClick={handleBack} aria-label="返回">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* form 包起來：手機鍵盤的「搜尋」鍵才會有作用 */}
        <form
          className="sp__field"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            submit(keyword);
            inputRef.current?.blur(); //送出後收鍵盤，讓人看得到結果
          }}
        >
          <svg className="sp__field-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.9" fill="none" />
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
          </svg>

          <input
            ref={inputRef}
            type="search"
            className="sp__input"
            placeholder="請輸入關鍵字"
            value={keyword}
            onChange={(e) => submit(e.target.value)}
            aria-label="搜尋命理服務"
          />
        </form>

        {/* 只在有字時才出現：空的時候放一顆按了沒反應的鈕沒有意義 */}
        {hasKeyword && (
          <button type="button" className="sp__clear" onClick={handleClear} aria-label="清除關鍵字">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" fill="none" />
              <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </header>

      {hasKeyword ? (
        //—— 有關鍵字：結果 ——
        <section className="sp__section">
          {results.length > 0 ? (
            <>
              <h2 className="sp__label">
                搜尋結果 <span className="sp__count">{results.length}</span>
              </h2>
              <div className="sp__results">
                {results.map((item) => (
                  <ProductCard key={item.id} item={item} />
                ))}
              </div>
            </>
          ) : (
            <div className="sp__empty">
              <span className="sp__empty-glyph" aria-hidden="true">尋</span>
              <p className="sp__empty-title">找不到「{keyword.trim()}」的結果</p>
              <p className="sp__empty-desc">換個說法，或從下面的推薦關鍵字開始。</p>

              <div className="sp__chips">
                {RECOMMEND_KEYWORDS.map((kw) => (
                  <button
                    key={kw}
                    type="button"
                    className="sp__chip"
                    onClick={() => submit(kw)}
                  >
                    {kw}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      ) : (
        //—— 沒有關鍵字：推薦與熱門 ——
        <>
          <section className="sp__section">
            <h2 className="sp__label">推薦關鍵字</h2>
            <div className="sp__chips">
              {RECOMMEND_KEYWORDS.map((kw) => (
                <button
                  key={kw}
                  type="button"
                  className="sp__chip"
                  onClick={() => submit(kw)}
                >
                  {kw}
                </button>
              ))}
            </div>
          </section>

          <section className="sp__section">
            <h2 className="sp__label">即時熱門</h2>
            <div className="sp__trending">
              {TRENDING.map((item) => (
                <FeatureCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        </>
      )}
    </main>
  );
};

export default SearchPage;
